/**
 * Pure parts of the in-app feedback loop (supabase/functions/feedback): what a report must look like,
 * the GitHub issue built from it (title, Hungarian body, labels), the comments written for the reporter,
 * and how an issue's state and labels become the status the app shows. No imports: tested from
 * packages/core/test/feedback.test.ts and used by the app's demo store.
 *
 * The issue never holds personal data: no e-mail address, no customer data, only the reporter's
 * e-mail domain, the workshop and its tags, the role and the technical details.
 */

export const FEEDBACK_KINDS = ["bug", "idea", "question"] as const;
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];
export const FEEDBACK_VERDICTS = ["ok", "notOk", "comment"] as const;
export type FeedbackVerdict = (typeof FEEDBACK_VERDICTS)[number];
/** What the app shows: new (or answered, not to check yet), waiting for the reporter's check, done, still failing. */
export type FeedbackStatus = "new" | "check" | "done" | "failing";

export const FEEDBACK_TITLE = { min: 3, max: 120 } as const;
export const FEEDBACK_TEXT = { min: 10, max: 4000 } as const;
/** "Még hibás" needs a short reason. */
export const FEEDBACK_NOT_OK_TEXT = { min: 5, max: 4000 } as const;
export const FEEDBACK_COMMENT = { min: 1, max: 4000 } as const;
/** A screenshot after decoding (the app sends at most 1600 px JPEG, typically 200 to 400 kB). */
export const FEEDBACK_SCREENSHOT_MAX_BYTES = 1_500_000;
/** The signed link to the screenshot in the issue is valid this long. */
export const FEEDBACK_SCREENSHOT_LINK_DAYS = 30;
/** At most this many reports are listed (GitHub rate limit friendly). */
export const FEEDBACK_LIST_MAX = 30;

/** Labels the developer and the function use. */
export const LABEL_FEEDBACK = "feedback";
/** The developer asks the reporter to check the fix. */
export const LABEL_CHECK = "ellenorizd";
/** The reporter said it is still wrong. */
export const LABEL_FAILING = "ujra-hibas";
export const LABEL_TEST = "teszt";
/** Workshop tag (workshop_billing.tags) of test workshops. */
export const TEST_TAG = "teszt";

/**
 * Hidden mark on everything the function writes for a reporter (the issue body and the reporter's
 * comments). The developer may use the same GitHub account, so authorship is told by this mark.
 */
export const REPORTER_MARK = "<!-- garagevo:reporter -->";

export const KIND_LABEL_HU: Record<FeedbackKind, string> = { bug: "Hiba", idea: "Ötlet", question: "Kérdés" };
/** Roles in the Hungarian issue text (the developer reads Hungarian; keep equal to role.<role>.label in hu.json). */
export const ROLE_HU: Record<string, string> = { owner: "Tulajdonos", manager: "Adminisztrátor", office: "Adminisztrátor", mechanic: "Szerelő" };
export const roleHu = (role: string | null | undefined) => ROLE_HU[role ?? ""] ?? (role || "?");

export interface ReportInput {
  kind?: unknown;
  title?: unknown;
  text?: unknown;
  page?: unknown;
  appVersion?: unknown;
  userAgent?: unknown;
}

export interface CleanReport {
  kind: FeedbackKind;
  title: string;
  text: string;
  page: string;
  appVersion: string;
  userAgent: string;
}

const str = (v: unknown) => (typeof v === "string" ? v : "");
/** One line, no control characters, at most n characters. */
const oneLine = (v: unknown, n: number) => str(v).replace(/[\u0000-\u001f\u007f]+/g, " ").replace(/\s+/g, " ").trim().slice(0, n);

/** A checked report, or the error code to answer with. */
export function cleanReport(input: ReportInput): CleanReport | { error: "REQUIRED_MISSING" | "NOT_ALLOWED" } {
  const kind = input.kind;
  if (!FEEDBACK_KINDS.includes(kind as FeedbackKind)) return { error: "NOT_ALLOWED" };
  const title = oneLine(input.title, 1000);
  const text = str(input.text).replace(/\r\n?/g, "\n").trim();
  if (title.length < FEEDBACK_TITLE.min || text.length < FEEDBACK_TEXT.min) return { error: "REQUIRED_MISSING" };
  if (title.length > FEEDBACK_TITLE.max || text.length > FEEDBACK_TEXT.max) return { error: "NOT_ALLOWED" };
  // the page is a hash route of the app ("#workOrders/<id>"), nothing else
  const page = /^#[\w/-]{0,200}$/.test(str(input.page).trim()) ? str(input.page).trim() : "#";
  return { kind: kind as FeedbackKind, title, text, page, appVersion: oneLine(input.appVersion, 60) || "?", userAgent: oneLine(input.userAgent, 300) || "?" };
}

/** The reply text checked for its verdict, or the error code. */
export function cleanReply(verdict: unknown, text: unknown): { verdict: FeedbackVerdict; text: string } | { error: "REQUIRED_MISSING" | "NOT_ALLOWED" } {
  if (!FEEDBACK_VERDICTS.includes(verdict as FeedbackVerdict)) return { error: "NOT_ALLOWED" };
  const v = verdict as FeedbackVerdict;
  const t = str(text).replace(/\r\n?/g, "\n").trim();
  const limits = v === "notOk" ? FEEDBACK_NOT_OK_TEXT : v === "comment" ? FEEDBACK_COMMENT : { min: 0, max: FEEDBACK_COMMENT.max };
  if (t.length < limits.min) return { error: "REQUIRED_MISSING" };
  if (t.length > limits.max) return { error: "NOT_ALLOWED" };
  return { verdict: v, text: t };
}

/** "szerelo@pelda.hu" → "pelda.hu"; anything else → "?". Never the address itself. */
export function emailDomain(email: string | null | undefined): string {
  const m = /@([a-z0-9.-]+\.[a-z]{2,})$/i.exec((email ?? "").trim());
  return m ? m[1]!.toLowerCase() : "?";
}

export const issueTitle = (title: string) => `[Garagevo] ${title}`;

export function issueLabels(kind: FeedbackKind, workshopTags: readonly string[]): string[] {
  const labels = [LABEL_FEEDBACK, kind];
  if (workshopTags.some((t) => t.trim().toLowerCase() === TEST_TAG)) labels.push(LABEL_TEST);
  return labels;
}

export interface IssueDetails {
  workshopName: string;
  workshopId: string;
  tags: readonly string[];
  role: string;
  emailDomain: string;
  /** ISO time of the report. */
  at: string;
  /** Signed link to the screenshot, when there is one. */
  screenshotUrl?: string | null;
}

/** Markdown cell/line text without characters that would break the table. */
const md = (s: string) => s.replace(/[|\r\n`]/g, " ").trim() || "?";

export function issueBody(report: CleanReport, d: IssueDetails): string {
  const rows: [string, string][] = [
    ["Fajta", KIND_LABEL_HU[report.kind]],
    ["Szerviz", d.workshopName],
    ["Szerviz azonosító", d.workshopId],
    ["Címkék", d.tags.length ? d.tags.join(", ") : "nincs"],
    ["Szerepkör", d.role],
    ["E-mail domain", d.emailDomain],
    ["Oldal", report.page],
    ["Verzió", report.appVersion],
    ["Böngésző", report.userAgent],
    ["Időpont", d.at],
  ];
  const parts = [
    REPORTER_MARK,
    report.text,
    "",
    ...(d.screenshotUrl ? [`**Képernyőkép** (a link ${FEEDBACK_SCREENSHOT_LINK_DAYS} napig él): ${d.screenshotUrl}`, ""] : []),
    "### Részletek",
    "",
    "| | |",
    "|---|---|",
    ...rows.map(([k, v]) => `| ${k} | \`${md(v)}\` |`),
    "",
    `_A programból küldve. Ha javítottad, tedd rá a \`${LABEL_CHECK}\` címkét, és a bejelentő a programban ellenőrzi._`,
  ];
  return parts.join("\n");
}

/** The reporter's own text from an issue body built by issueBody (the part before the details). */
export function reportTextFromBody(body: string | null | undefined): string {
  const b = (body ?? "").replace(REPORTER_MARK, "");
  const cut = b.search(/\n\*\*Képernyőkép\*\*|\n### Részletek/);
  return (cut >= 0 ? b.slice(0, cut) : b).trim();
}

/** The comment the function writes for a reporter's answer. */
export function replyComment(verdict: FeedbackVerdict, role: string, text: string): string {
  const head = verdict === "ok" ? `✅ Ellenőrizve, jó (név nélkül: ${role})` : verdict === "notOk" ? `❌ Még hibás (név nélkül: ${role})` : `💬 Hozzászólás (név nélkül: ${role})`;
  return [REPORTER_MARK, head, ...(text ? ["", text] : [])].join("\n");
}

/** Whether a comment was written by the program for a reporter (else it is the developer's answer). */
export const isReporterComment = (body: string | null | undefined) => (body ?? "").includes(REPORTER_MARK);

/** A comment as the app shows it: without the hidden mark. */
export const commentText = (body: string | null | undefined) => (body ?? "").replace(REPORTER_MARK, "").trim();

/** GitHub issue state and labels → what the app shows. */
export function feedbackStatus(state: string | null | undefined, labels: readonly string[]): FeedbackStatus {
  if (state === "closed") return "done";
  const l = labels.map((x) => x.toLowerCase());
  if (l.includes(LABEL_CHECK)) return "check";
  if (l.includes(LABEL_FAILING)) return "failing";
  return "new";
}

/** The status kept in public.feedback_reports for a shown status ('verified' once the reporter accepted it). */
export const storedStatus = (s: FeedbackStatus, verified = false): "open" | "check" | "done" | "verified" =>
  verified ? "verified" : s === "check" ? "check" : s === "done" ? "done" : "open";

/** Label changes of a verdict: what to add, what to remove, and the issue state after it. */
export function verdictChanges(verdict: FeedbackVerdict): { add: string[]; remove: string[]; state: "open" | "closed" | null } {
  if (verdict === "ok") return { add: [], remove: [LABEL_CHECK, LABEL_FAILING], state: "closed" };
  if (verdict === "notOk") return { add: [LABEL_FAILING], remove: [LABEL_CHECK], state: "open" };
  return { add: [], remove: [], state: null };
}

/**
 * A base64 screenshot (optionally a data URL): JPEG or PNG by its first bytes, at most
 * FEEDBACK_SCREENSHOT_MAX_BYTES. Returns the bytes and type, or an error code.
 */
export function decodeScreenshot(data: unknown): { bytes: Uint8Array; mediaType: "image/jpeg" | "image/png"; ext: "jpg" | "png" } | { error: "NOT_ALLOWED" } {
  if (typeof data !== "string" || !data) return { error: "NOT_ALLOWED" };
  const b64 = data.replace(/^data:[\w/+.-]+;base64,/, "").replace(/\s+/g, "");
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(b64) || b64.length > Math.ceil((FEEDBACK_SCREENSHOT_MAX_BYTES * 4) / 3) + 4) return { error: "NOT_ALLOWED" };
  let bin: string;
  try {
    bin = atob(b64);
  } catch {
    return { error: "NOT_ALLOWED" };
  }
  if (bin.length > FEEDBACK_SCREENSHOT_MAX_BYTES) return { error: "NOT_ALLOWED" };
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return { bytes, mediaType: "image/jpeg", ext: "jpg" };
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return { bytes, mediaType: "image/png", ext: "png" };
  return { error: "NOT_ALLOWED" };
}
